import { PriceRecalculationResult } from "src/api/generated/erp/sales/pricing/service/priceRecalculationResult.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useAavoToast } from "src/components/common/toast/AavoToastContext.tsx";
import { faBellSlash, faRotate } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { useIsMobile } from "src/utils/breakpointUtils.ts";
import { AavoMenuDivider } from "src/components/common/menu/AavoMenuDivider.tsx";
import type { PriceRecalculationStatus } from "src/components/views/erp/sales/pricing/priceRecalculationActionVisibility.ts";

export interface PriceRecalculationActionsProps {
	status: PriceRecalculationStatus;
	recalculatePrices: () => Promise<PriceRecalculationResult>;
	acknowledgePriceRecalculation: () => Promise<void>;
	onStatusChanged: () => Promise<unknown>;
}

export function PriceRecalculationActions({
	status,
	recalculatePrices,
	acknowledgePriceRecalculation,
	onStatusChanged,
}: PriceRecalculationActionsProps) {
	const showConfirmDialog = useConfirmDialog();
	const { showToast } = useAavoToast();
	const isMobile = useIsMobile();

	if (isMobile) return null;

	async function recalculate() {
		const result = await recalculatePrices();
		showToast({
			title: i18n.t("prices_recalculated"),
			message: formatPriceRecalculationResult(result),
		});
		await onStatusChanged();
	}

	async function acknowledge() {
		const confirmed = await showConfirmDialog({
			title: i18n.t("acknowledge_price_recalculation"),
			message: i18n.t("acknowledge_price_recalculation_confirmation"),
		});
		if (!confirmed) return;

		await acknowledgePriceRecalculation();
		await onStatusChanged();
	}

	const showAcknowledge = status === "RECALCULATION_REQUIRED";
	return (
		<>
			<AavoMenuDivider />
			<AsyncMenuButton label={i18n.t("recalculate_prices")} icon={faRotate} onClick={recalculate} />
			{showAcknowledge && (
				<AsyncMenuButton
					icon={faBellSlash}
					label={i18n.t("acknowledge_price_recalculation")}
					onClick={acknowledge}
				/>
			)}
		</>
	);
}

function formatPriceRecalculationResult(result: PriceRecalculationResult): string {
	const { costRefresh } = result;
	const messages = [
		i18n.t("profit_margin_prices_recalculated_message", {
			recalculatedLineCount: result.recalculatedLineCount,
			skippedLineCount: result.skippedLineCount,
		}),
		i18n.t("part_cost_override_refresh_updated_lines", { count: costRefresh.updatedLineCount }),
	];
	if (costRefresh.keptPurchasedOrProducedConfigurationCount > 0)
		messages.push(
			i18n.t("part_cost_override_refresh_kept_purchased_or_produced_configurations", {
				count: costRefresh.keptPurchasedOrProducedConfigurationCount,
			}),
		);
	return messages.join(" ");
}
